import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ShoppingBag, ArrowLeft, Zap, Shield, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useProducts } from '../context/ProductsContext';
import ProductCard from '../components/ProductCard';

const ProductDetailPage = ({ onAddToCart }) => {
  const { id } = useParams();
  const { products } = useProducts();
  // eslint-disable-next-line eqeqeq
  const product = products.find(p => p.id == id);
  const [qty, setQty] = useState(1);
  const [isSubscribe, setIsSubscribe] = useState(false);
  const [added, setAdded] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [imgErrors, setImgErrors] = useState({});

  if (!product) return (
    <div style={{ paddingTop: '120px', textAlign: 'center' }}>
      <h2>Product not found</h2>
      <Link to="/shop">Back to Shop</Link>
    </div>
  );

  const gallery = [
    product.image,
    product.image ? product.image + (product.image.includes('?') ? '&view=2' : '?view=2') : null,
    product.image ? product.image + (product.image.includes('?') ? '&view=3' : '?view=3') : null,
  ].filter(Boolean);

  const handleAdd = () => {
    setAdded(true);
    onAddToCart && onAddToCart(product, qty);
    setTimeout(() => setAdded(false), 2000);
  };

  const prevImg = () => setActiveImg(i => (i - 1 + gallery.length) % gallery.length);
  const nextImg = () => setActiveImg(i => (i + 1) % gallery.length);

  const faqs = [
    { q: 'Is this product legal?', a: 'Yes. All Mind Blown products are compliant with the 2018 Farm Bill. They contain hemp-derived cannabinoids with less than 0.3% Delta-9 THC by dry weight (or in compliant servings for drinks).' },
    { q: 'Will this show up on a drug test?', a: 'THC products, including hemp-derived Delta-9, may cause a positive result on a drug test. We advise caution if you are subject to drug testing.' },
    { q: 'Can you ship to my state?', a: 'We ship to most states, but laws vary. Our checkout automatically restricts orders to states where your product is legal. Enter your address for real-time eligibility.' },
    { q: 'Where can I view the lab results?', a: 'Every product has a batch-specific Certificate of Analysis on our Lab Results page, or you can scan the QR code on the packaging.' },
  ];

  const related = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 3);

  return (
    <div style={{ paddingTop: '68px', minHeight: '100vh', background: 'var(--cream)' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: 'clamp(20px,4vw,40px) clamp(16px,4vw,32px)' }}>

        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '28px' }}>
          <Link to="/shop" style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            color: 'var(--mid)', fontSize: '0.85rem',
            textDecoration: 'none', transition: 'color 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--charcoal)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--mid)'}>
            <ArrowLeft size={14} /> Shop
          </Link>
          <span style={{ color: 'var(--mist)' }}>/</span>
          <span style={{ fontSize: '0.85rem', color: 'var(--charcoal)', fontWeight: 500 }}>{product.name}</span>
        </div>

        {/* ── Main grid ── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'clamp(24px, 5vw, 64px)',
          marginBottom: '80px',
          alignItems: 'start',
        }} className="detail-grid">

          {/* ── LEFT: image gallery ── */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45 }}
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
          >
            {/* Main image with arrows */}
            <div style={{
              background: product.gradient,
              borderRadius: '24px',
              overflow: 'hidden',
              position: 'relative',
              border: '1px solid var(--mist)',
              aspectRatio: '1 / 1',
              width: '100%',
            }}>
              {/* Badge */}
              {product.badge && (
                <div style={{
                  position: 'absolute', top: '16px', left: '16px', zIndex: 2,
                  background: product.badgeColor, color: 'var(--white)',
                  borderRadius: '999px', padding: '5px 14px',
                  fontSize: '0.72rem', fontWeight: 700,
                  fontFamily: 'Syne', letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}>{product.badge}</div>
              )}

              {/* Discount badge */}
              {product.originalPrice && (
                <div style={{
                  position: 'absolute', top: '16px', right: '16px', zIndex: 2,
                  background: 'var(--white)', color: '#e05c3a',
                  borderRadius: '6px', padding: '4px 10px',
                  fontSize: '0.72rem', fontWeight: 700,
                }}>
                  -{Math.round((1 - product.price / product.originalPrice) * 100)}% OFF
                </div>
              )}

              {/* Left arrow */}
              <button
                onClick={prevImg}
                style={{
                  position: 'absolute', left: '12px', top: '50%',
                  transform: 'translateY(-50%)', zIndex: 3,
                  width: '36px', height: '36px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.88)',
                  border: '1px solid var(--mist)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                  transition: 'background 0.2s, transform 0.15s',
                  backdropFilter: 'blur(4px)',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--white)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.88)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 12L6 8L10 4" stroke="var(--charcoal)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {/* Right arrow */}
              <button
                onClick={nextImg}
                style={{
                  position: 'absolute', right: '12px', top: '50%',
                  transform: 'translateY(-50%)', zIndex: 3,
                  width: '36px', height: '36px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.88)',
                  border: '1px solid var(--mist)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
                  transition: 'background 0.2s, transform 0.15s',
                  backdropFilter: 'blur(4px)',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--white)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.88)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 4L10 8L6 12" stroke="var(--charcoal)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {/* Main image */}
              {!imgErrors[activeImg] && gallery[activeImg] ? (
                <img
                  key={activeImg}
                  src={gallery[activeImg]}
                  alt={product.name}
                  onError={() => setImgErrors(prev => ({ ...prev, [activeImg]: true }))}
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover', objectPosition: 'center',
                    display: 'block',
                  }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: product.gradient,
                  minHeight: '320px',
                }}>
                  <span style={{ fontSize: '0.9rem', color: 'var(--mid)', fontFamily: 'Syne', fontWeight: 600, padding: '16px', textAlign: 'center' }}>
                    {product.name}
                  </span>
                </div>
              )}

              {/* Dot indicators */}
              <div style={{
                position: 'absolute', bottom: '14px', left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex', gap: '6px', zIndex: 3,
              }}>
                {gallery.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    style={{
                      width: activeImg === i ? '20px' : '6px',
                      height: '6px',
                      borderRadius: '999px',
                      background: activeImg === i ? 'var(--forest)' : 'rgba(255,255,255,0.75)',
                      border: 'none', padding: 0, cursor: 'pointer',
                      transition: 'all 0.25s',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Thumbnails — compact fixed-size strip */}
            <div style={{ display: 'flex', gap: '8px' }}>
              {gallery.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  style={{
                    width: '64px',
                    height: '64px',
                    flexShrink: 0,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    border: activeImg === i
                      ? '2.5px solid var(--forest)'
                      : '2px solid var(--mist)',
                    background: product.gradient,
                    cursor: 'pointer',
                    padding: 0,
                    opacity: activeImg === i ? 1 : 0.55,
                    transition: 'border-color 0.2s, opacity 0.2s, transform 0.15s',
                    transform: activeImg === i ? 'scale(1.05)' : 'scale(1)',
                  }}
                  onMouseEnter={e => { if (i !== activeImg) e.currentTarget.style.opacity = '0.85'; }}
                  onMouseLeave={e => { if (i !== activeImg) e.currentTarget.style.opacity = '0.55'; }}
                >
                  {!imgErrors[i] && src ? (
                    <img
                      src={src}
                      alt={`${product.name} view ${i + 1}`}
                      onError={() => setImgErrors(prev => ({ ...prev, [i]: true }))}
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'cover', objectPosition: 'center',
                        display: 'block',
                      }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: product.gradient }} />
                  )}
                </button>
              ))}
            </div>

            {/* Lab badge */}
            <div style={{
              background: 'var(--white)',
              borderRadius: '16px', padding: '16px 20px',
              border: '1px solid var(--mist)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px', height: '36px',
                  background: '#e8f5e9', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Shield size={16} color="var(--accent-green)" />
                </div>
                <div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '0.85rem', color: 'var(--charcoal)' }}>
                    Third-Party Lab Tested
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--mid)' }}>
                    Certificate of Analysis available
                  </div>
                </div>
              </div>
              <Link to="/lab-results" style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                fontSize: '0.78rem', color: 'var(--accent-green)',
                fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap',
              }}>
                View COA <ExternalLink size={12} />
              </Link>
            </div>
          </motion.div>

          {/* ── RIGHT: product info ── */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '0.72rem', fontWeight: 700,
                color: 'var(--mid)', textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>{product.category}</span>
              <span style={{
                fontSize: '0.72rem', fontWeight: 600,
                color: product.effectColor,
                background: product.effectColor + '18',
                padding: '3px 10px', borderRadius: '999px',
              }}>{product.effect}</span>
            </div>

            <h1 style={{
              fontFamily: 'Syne', fontWeight: 700,
              fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
              color: 'var(--charcoal)', letterSpacing: '-0.01em',
              marginBottom: '14px', lineHeight: '1.1',
            }}>{product.name}</h1>

            {/* Rating */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: '3px' }}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14}
                    fill={i < Math.floor(product.rating) ? 'var(--gold)' : 'transparent'}
                    color="var(--gold)"
                  />
                ))}
              </div>
              <span style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--charcoal)' }}>
                {product.rating}
              </span>
              <span style={{ fontSize: '0.85rem', color: 'var(--mid)' }}>
                ({product.reviews} reviews)
              </span>
            </div>

            {/* Quick specs */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '24px', flexWrap: 'wrap' }}>
              {[
                { label: 'Potency', value: product.mg },
                { label: 'Onset', value: product.onset },
                { label: 'Effect', value: product.effect },
              ].map(spec => (
                <div key={spec.label} style={{
                  background: 'var(--smoke)',
                  borderRadius: '12px', padding: '12px 16px',
                  border: '1px solid var(--mist)',
                  minWidth: '80px',
                }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--mid)', fontWeight: 600, marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {spec.label}
                  </div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '0.9rem', color: 'var(--charcoal)' }}>
                    {spec.value}
                  </div>
                </div>
              ))}
            </div>

            <p style={{
              fontSize: '0.95rem', color: 'var(--mid)', lineHeight: '1.8',
              marginBottom: '32px',
            }}>
              {product.description} Each batch is tested by an accredited third-party laboratory for potency, heavy metals, pesticides, and microbials before it ever reaches you.
            </p>

            {/* Price */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' }}>
              <span style={{
                fontFamily: 'Syne', fontWeight: 700, fontSize: '2rem',
                color: 'var(--forest)',
              }}>${isSubscribe ? (product.price * 0.85).toFixed(2) : product.price}</span>
              {product.originalPrice && !isSubscribe && (
                <span style={{
                  fontSize: '1rem', color: 'var(--light-text)',
                  textDecoration: 'line-through',
                }}>${product.originalPrice}</span>
              )}
              {isSubscribe && (
                <span style={{
                  fontSize: '0.82rem', color: 'var(--accent-green)',
                  fontWeight: 600, background: '#e8f5e9',
                  padding: '3px 10px', borderRadius: '999px',
                }}>15% subscribe discount</span>
              )}
            </div>

            {/* Subscribe toggle */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '28px' }}>
              {[
                { label: 'One-Time', sub: false },
                { label: 'Subscribe & Save', sub: true },
              ].map(opt => (
                <button
                  key={opt.label}
                  onClick={() => setIsSubscribe(opt.sub)}
                  style={{
                    padding: '14px',
                    borderRadius: '14px',
                    background: isSubscribe === opt.sub ? 'var(--forest)' : 'var(--smoke)',
                    color: isSubscribe === opt.sub ? 'var(--white)' : 'var(--mid)',
                    border: isSubscribe === opt.sub ? 'none' : '1.5px solid var(--mist)',
                    fontFamily: 'Syne', fontWeight: 600, fontSize: '0.85rem',
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  {opt.label}
                  {opt.sub && <div style={{ fontSize: '0.7rem', opacity: 0.7, fontWeight: 400 }}>Save 15% monthly</div>}
                </button>
              ))}
            </div>

            {/* Qty + Add to cart */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                display: 'flex', alignItems: 'center',
                border: '1.5px solid var(--mist)',
                borderRadius: '999px', background: 'var(--white)',
                overflow: 'hidden', flexShrink: 0,
              }}>
                <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{
                  width: '44px', height: '48px', background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: '1.2rem', color: 'var(--charcoal)', fontWeight: 300,
                }}>−</button>
                <span style={{
                  width: '36px', textAlign: 'center',
                  fontFamily: 'Syne', fontWeight: 700, fontSize: '0.95rem',
                }}>{qty}</span>
                <button onClick={() => setQty(q => q + 1)} style={{
                  width: '44px', height: '48px', background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: '1.2rem', color: 'var(--charcoal)', fontWeight: 300,
                }}>+</button>
              </div>
              <button
                onClick={handleAdd}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: '10px',
                  background: added ? 'var(--accent-green)' : 'var(--forest)',
                  color: 'var(--white)', border: 'none',
                  borderRadius: '999px', padding: '14px 20px',
                  fontFamily: 'Syne', fontWeight: 700, fontSize: '0.9rem',
                  letterSpacing: '0.04em', textTransform: 'uppercase',
                  cursor: 'pointer', transition: 'all 0.25s',
                  whiteSpace: 'nowrap',
                }}
              >
                <ShoppingBag size={16} />
                {added ? '✓ Added to Cart!' : 'Add to Cart'}
              </button>
            </div>

            {/* Delivery note */}
            <div style={{
              background: '#fff9e6', borderRadius: '12px',
              padding: '14px 18px',
              border: '1px solid #ffe4a0',
              display: 'flex', alignItems: 'center', gap: '10px',
            }}>
              <Zap size={14} color="var(--gold)" style={{ flexShrink: 0 }} />
              <span style={{ fontSize: '0.82rem', color: 'var(--charcoal)' }}>
                <strong>Adult signature required</strong> upon delivery.
                Orders ship within 1–2 business days.
              </span>
            </div>
          </motion.div>
        </div>

        {/* ── FAQ ── */}
        <div style={{ maxWidth: '720px', margin: '0 auto 80px' }}>
          <h2 style={{
            fontFamily: 'Syne', fontWeight: 700, fontSize: 'clamp(1.3rem,3vw,1.6rem)',
            color: 'var(--forest)', letterSpacing: '-0.01em',
            marginBottom: '28px', textAlign: 'center',
          }}>Common Questions</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={{
                background: 'var(--white)', borderRadius: '16px',
                border: '1px solid var(--mist)', overflow: 'hidden',
              }}>
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  style={{
                    width: '100%', padding: '20px 24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontFamily: 'Syne', fontWeight: 600, fontSize: 'clamp(0.82rem,2vw,0.92rem)',
                    color: 'var(--charcoal)', textAlign: 'left', gap: '12px',
                  }}
                >
                  {faq.q}
                  {faqOpen === i ? <ChevronUp size={16} color="var(--mid)" /> : <ChevronDown size={16} color="var(--mid)" />}
                </button>
                {faqOpen === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    style={{ padding: '0 24px 20px' }}
                  >
                    <p style={{ fontSize: '0.88rem', color: 'var(--mid)', lineHeight: '1.75' }}>
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Related products ── */}
        {related.length > 0 && (
          <div style={{ marginBottom: '48px' }}>
            <h2 style={{
              fontFamily: 'Syne', fontWeight: 700, fontSize: 'clamp(1.3rem,3vw,1.6rem)',
              color: 'var(--forest)', letterSpacing: '-0.01em',
              marginBottom: '28px',
            }}>You Might Also Like</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '20px',
            }} className="related-grid">
              {related.map(p => (
                <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 860px) {
          .detail-grid {
            grid-template-columns: 1fr !important;
            gap: 28px !important;
          }
          .related-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 500px) {
          .related-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductDetailPage;